import { inquiryStatusConfig, BRAND } from "../types";
import type { InquiryOrder } from "../types";
import InquiryProgress from "./InquiryProgress";

export default function InquiryCard({ order, onClick }: { order: InquiryOrder; onClick: () => void }) {
  const cfg = inquiryStatusConfig[order.status];
  return (
    <div
      className="rounded-2xl overflow-hidden cursor-pointer active:scale-[0.99] transition-all"
      style={{ background: "#fff", border: "1.5px solid #F0F0F0" }}
      onClick={onClick}
    >
      {/* 头部 */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-sm font-bold text-gray-900">
                {order.needs.destination} · {order.needs.dates.split("（")[0]}
              </span>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                style={{ background: cfg.bg, color: cfg.color }}
              >
                {cfg.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              <span className="text-[11px] text-gray-400 whitespace-nowrap">询单号</span>
              <span className="inline-flex items-center gap-0.5 text-[11px] font-mono font-medium px-1.5 py-px rounded" style={{ background: "#EFF1F4", color: "#4B5563" }}>
                {order.orderNo || order.id}
              </span>
              <span className="text-[10px] text-gray-300 mx-0.5">·</span>
              <span className="text-[11px] text-gray-400">
                {(() => {
                  const d = order.createTime;
                  return d.replace(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}:\d{2}).*$/, "$2-$3 $4");
                })()}
              </span>
            </div>
          </div>
          <i className="ri-arrow-right-s-line text-gray-300 flex-shrink-0 mt-0.5" style={{ fontSize: 18 }} />
        </div>

        {/* 进度条 */}
        <InquiryProgress status={order.status} />

        {/* AI 摘要（task_params_text） */}
        {order.aiSummary && (
          <div className="mt-2.5 px-3 py-2.5 rounded-xl"
            style={{ background: "linear-gradient(135deg,#FAFBFF 0%,#F5F7FB 100%)", border: "1px solid #EEF0F6" }}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <i className="ri-sparkling-line" style={{ color: "#8B95A5", fontSize: 11 }} />
              <span className="text-[10px] font-medium" style={{ color: "#94A3B8" }}>需求摘要</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {order.aiSummary.split(/,\s*/).filter(Boolean).map((seg, i) => {
                const ci = seg.indexOf(":");
                if (ci === -1) return null;
                const label = seg.slice(0, ci).trim();
                const val = seg.slice(ci + 1).trim();
                if (!val) return null;
                return (
                  <span key={i} className="inline-flex items-center text-[11px] px-2 py-1 rounded-md whitespace-nowrap font-medium"
                    style={{
                      background: "#fff",
                      border: `1px solid ${i % 3 === 0 ? "#E0E5EC" : i % 3 === 1 ? "#FDE8D4" : "#DBEAFE"}`,
                      color: i % 3 === 0 ? "#475569" : i % 3 === 1 ? "#B45309" : "#2563EB",
                    }}
                  >
                    <span className="opacity-50 mr-1">{label}</span>
                    <span>{val}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* 状态说明 */}
        <div
          className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: `${cfg.color}0C`, border: `1px solid ${cfg.color}22` }}
        >
          <i className={cfg.icon} style={{ color: cfg.color, fontSize: 13, flexShrink: 0, marginTop: 1 }} />
          <span className="text-xs leading-relaxed" style={{ color: cfg.color }}>{cfg.desc}</span>
        </div>
      </div>

      {/* 金额 & 操作 */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ background: "#FAFBFC", borderTop: "1px solid #F3F4F6" }}
      >
        <div>
          {order.totalQuotedPrice !== undefined ? (
            <>
              <div className="text-xs text-gray-400 mb-0.5">顾问报价总额</div>
              <div className="text-base font-bold" style={{ color: "#EF4444" }}>
                ¥{order.totalQuotedPrice.toLocaleString()}
              </div>
            </>
          ) : (
            <>
              <div className="text-xs text-gray-400 mb-0.5">参考预算</div>
              <div className="text-sm font-medium text-gray-600">
                ¥{parseInt(order.needs.budget).toLocaleString()}
              </div>
            </>
          )}
          <div className="text-xs text-gray-400 mt-0.5">
            {order.needs.travelers}人 · {order.needs.destination}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {["待处理", "处理中", "待填写"].includes(order.status) && (
            <div
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
              style={{ background: "#FEF3C7", color: "#D97706" }}
            >
              <i className="ri-time-line" style={{ fontSize: 12 }} />
              {order.status === "待填写" ? "去完善信息" : "等待顾问报价"}
            </div>
          )}
          {(order.status === "处理中" || order.status === "待支付" || order.status === "待使用") && (
            <div
              className="px-4 py-2 rounded-xl text-xs font-bold text-white whitespace-nowrap"
              style={{ background: `linear-gradient(135deg, ${BRAND}, #FF9A5C)` }}
            >
              {order.status === "待使用" ? "查看订单" : order.status === "待支付" ? "去支付" : "查看方案"}
            </div>
          )}
          {["已完成", "已取消", "已退款", "无效"].includes(order.status) && (
            <div
              className="px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap"
              style={{ background: "#F3F4F6", color: "#374151" }}
            >
              查看详情
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
